/**
 * English content.
 *
 * One file per language, unlike French which is split for historical reasons.
 * Adding a language means adding a file here and one entry in the registry:
 * nothing else moves.
 */
import type { LanguageContent } from '../registry.js';

export const EN: LanguageContent = {
  home: {
    intro: [
      'Retusch is an online drawing game for 2 to 10 players, played in the browser, with nothing to install and no account to create. Each round hands you a painting with one square erased: repaint it so it vanishes into the artwork. Then the roles flip, and you have to flush out everyone else’s patches.',
      'Most drawing games reward whoever gets recognised fastest. Retusch asks for the opposite: winning means not being seen.',
    ],
    steps: [
      {
        title: 'A painting with a hole',
        text: 'You get an illustration with one square erased.',
        painting: '1_airIsland',
        stage: 'masked',
      },
      {
        title: 'You repaint it',
        text: 'You redraw the square so it blends into the artwork.',
        painting: '1_airIsland',
        stage: 'painted',
      },
      {
        title: 'The hunt',
        text: 'The painting comes back with everyone’s patches: find them before the others do.',
        painting: '1_airIsland',
        stage: 'hunt',
      },
      {
        title: 'The reveal',
        text: 'Every hiding place is uncovered, and the points land.',
        painting: '1_airIsland',
        stage: 'reveal',
      },
    ],
    modes: [
      {
        name: 'Shared hunt',
        text: 'Everyone paints on the same artwork, each in their own square. Then the hunt opens for all at once: a patch can only be found once, by whoever spots it first. This is the default mode, the one where something is always happening.',
      },
      {
        name: 'Stream',
        text: 'The host does not draw: they search alone while everyone else hides inside their painting. Built for live shows, where the hunt becomes the entertainment. Free forever.',
      },
      {
        name: 'One painting each',
        text: 'One artwork per player. You only hunt for one person’s patch, in a single click. The simplest rule in the game, and the tensest.',
      },
      {
        name: 'One draws',
        text: 'One round, one artist, everyone else chasing. The role rotates, and the artist watches their own hiding place fall or hold.',
      },
      {
        name: 'Battle royale',
        text: 'The same hunt, except the first patch uncovered knocks its author out. You play down to the last one standing, without counting a single point.',
      },
      {
        name: 'Contest',
        text: 'Nobody searches. Everyone repaints the same square of the same artwork, then votes for the patch they find best. One vote earns one point.',
      },
    ],
    faq: [
      {
        question: 'How many players do you need?',
        answer:
          'From 2 to 10. If you are on your own, an automatic opponent takes the empty seat so the game can start right away. From four players up, the shared hunt really comes into its own.',
      },
      {
        question: 'Is it free?',
        answer:
          'Yes. Quick play, the shared hunt and stream mode are free, with no limit on time or number of games. The other modes are free too, for now.',
      },
      {
        question: 'Do I need an account?',
        answer:
          'No. Type a nickname, click, play. An account only keeps your nickname and avatar from one game to the next: it stays optional.',
      },
      {
        question: 'Does it work on a phone?',
        answer:
          'Yes, the interface adapts to touch: folded toolbar, magnifier under your finger, double tap to zoom. That said, you draw more precisely with a mouse and a large screen.',
      },
      {
        question: 'How do I invite my friends?',
        answer:
          'Create a room, copy the invite link and paste it wherever you like. Your friends land straight at the table, with no account and nothing to install.',
      },
      {
        question: 'What is a “patch”?',
        answer:
          'It is what you paint inside the erased square. Done well, it passes for a piece of the original artwork and nobody finds it. Done badly, it leaps off the screen.',
      },
    ],
    headings: {
      intro: 'A drawing game where you have to disappear',
      steps: 'How to play',
      modes: 'Six ways to play',
      faq: 'Frequently asked questions',
      more: 'Read more',
    },
    links: {
      rules: 'Read the full rules and the scoring',
      modes: 'Compare the modes in detail',
    },
  },

  howToPlay: {
    title: 'How to play Retusch',
    summary:
      'The full rules of Retusch: how a round unfolds, the scoring on both sides, the drawing tools, and advice on hiding as well as hunting.',
    sections: [
      {
        heading: 'The idea in three sentences',
        blocks: [
          'You are given a painting with one square erased, and you repaint it so it goes unnoticed.',
          'Then everyone looks at the retouched paintings and hunts for the repainted squares.',
          'You score by finding fast, and you also score for as long as your own hiding place holds.',
        ],
      },
      {
        heading: 'How a round unfolds',
        blocks: [
          'A game strings together identical rounds, five by default. Each one goes through three moments.',
          {
            list: [
              'Drawing. You receive your painting and your square, and you have 2 minutes by default to repaint it. The Done button counts as submitting: once everyone has submitted, the round moves on without waiting for the clock.',
              'The hunt. The painting reappears with the patches, and you have 30 seconds by default to spot them. Clicking a repainted square reveals it to the whole table.',
              'The reveal. Every hiding place is framed and named, including the ones nobody saw. A slider lets you compare the retouched painting with the original artwork.',
            ],
          },
        ],
      },
      {
        heading: 'The scoring',
        blocks: [
          'Two counters run at the same time, and they pull against each other: the hunter wants speed, the artist wants their square to hold.',
          {
            list: [
              'As a hunter: 1000 points if you find it within the first 3 seconds, then a steady decline down to 100 points at the very end of the phase. Missing or finding nothing earns you nothing.',
              'As an artist: only 100 points if your square falls immediately, then a steady climb up to 800 points if it holds to the end. A patch nobody finds is worth the maximum, 800 points.',
            ],
          },
          'The artist is paid once only, at the reveal, and on the fastest hunter. So it makes no difference whether two or eight people are looking for you: only the one who finds you first counts.',
          'The scoring is the same whatever duration you set for the phase. Making the hunt longer does not move the two bounds, only the slope between them.',
          'One mistake per round. The first click that touches no patch ends your hunt: without that rule, you would rake the image at random until you stumbled onto something. Two gestures cost nothing: clicking your own square, and clicking a patch that has already been found.',
        ],
      },
      {
        heading: 'The drawing tools',
        blocks: [
          {
            list: [
              'Brush (B): three quick sizes, plus a slider for the exact size. Shortcuts 1, 2 and 3 pick the three sizes.',
              'Paint bucket (P): fills a flat area in one go, never spilling outside your square.',
              'Eyedropper (I): picks a colour anywhere on the painting. Holding Alt switches to it for one click, then hands back to the previous tool.',
              'Hand (H): moves the view. Outside your square, the brush and the bucket do the same thing, so you almost never need to switch tools.',
              'Undo (Ctrl+Z or Cmd+Z): steps back one stroke at a time, up to thirty times. There is no redo.',
              'The background swatch, at the end of the palette: it is the starting colour of your square. Painting with it amounts to erasing, except that it is a stroke like any other, which can be undone and painted over. That is why there is no eraser.',
            ],
          },
        ],
      },
      {
        heading: 'Five tips for hiding',
        blocks: [
          {
            list: [
              'Pick colours, do not guess them. The eyedropper gives you the exact colour of the surroundings: a flat area that is even slightly off is spotted from far away, while an exact one becomes invisible.',
              'Continue whatever crosses your square. A wall, a horizon, a branch entering on one side has to come out on the other in the right place. A broken line is the first clue an experienced player looks for.',
              'Paint the corners first. This is the most common mistake: the middle is neat, the four corners stay at the original background, and the square shows up plainly without anyone even looking at the drawing.',
              'Work from broad to fine. Lay down the large masses of colour with a big brush first, then refine. The other way round eats all your time on one corner while the rest stays empty.',
              'Match the level of detail around you. A square that is too clean in the middle of a busy area shows up just as much as a sloppy one in the middle of a flat colour.',
            ],
          },
        ],
      },
      {
        heading: 'Four tips for hunting',
        blocks: [
          {
            list: [
              'Look for lines that stop. A border, a cable, a table edge shifting by a hair: that is almost always where it is.',
              'Sweep first, zoom later. At full view, a badly blended square looks like a stain. Zoom is for confirming, not for searching.',
              'Be wary of the too smooth. A perfectly even surface in the middle of a texture is as suspicious as a smudge.',
              'Do not click at random. You only get one mistake: three seconds of checking cost less than the whole round.',
            ],
          },
        ],
      },
      {
        heading: 'What changes by mode?',
        blocks: [
          'Everything above describes the shared hunt, the default mode. The other modes change who draws, who searches and how you win: each one is detailed on the game modes page.',
        ],
      },
    ],
  },

  modes: {
    title: 'Retusch game modes',
    summary:
      'The six ways to play Retusch: shared hunt, stream, one painting each, one draws, battle royale and contest. How each one runs, the recommended player count and the length.',
    sections: [
      {
        heading: 'Shared hunt',
        blocks: [
          'The default mode, and the one to start with.',
          'Everyone draws on the same painting, each in their own square. Once the patches are in, the artwork comes back whole and the hunt opens for everyone at once. A patch can only be found once, by whoever spots it first: it then appears for the whole table and leaves the game. You never find your own, and a hiding place nobody sees earns its author the maximum.',
          'Best for a large group: there is no downtime, everyone searches at the same time, and every discovery shows up on everybody’s screen.',
          'Recommended: 3 to 10 players.',
        ],
      },
      {
        heading: 'Stream',
        blocks: [
          'The mirror image of “one draws”, built for live shows.',
          'Every player draws on the same painting except the host. Then the host is the only one hunting, while their audience watches them struggle. They score no points and do not appear in the standings: the game is played between those who hide, the host runs the show. They can be wrong as often as they like, since the one-mistake rule does not apply to them.',
          'Best for a live show, and for one specific reason: because the host does not draw, the audience never watches their hiding place being built. In the other modes, they watch the streamer paint their square, and the hunt holds no secret for them.',
          'Recommended: 4 to 10 players. Free, and staying that way.',
        ],
      },
      {
        heading: 'One painting each',
        blocks: [
          'Every player gets their own artwork and hides their patch in it. At hunt time, you are handed one other player’s painting, and you have a single click to point at the repainted square. No race, no competition: just you, a painting, and a decision.',
          'Best as a duo, or when you want a calm game where everyone takes their time.',
          'Recommended: 2 to 10 players.',
        ],
      },
      {
        heading: 'One draws',
        blocks: [
          'One round, one artist, everyone else chasing.',
          'Each round, a single person receives a painting. The others wait, then hunt for their patch, each scored on their own time. The artist is scored on the fastest hunter, and watches their frozen hiding place while it is being tracked. The role rotates: the round setting counts full cycles here, because a game is only fair if everyone has drawn the same number of times.',
          'Best for a small group that wants to be judged on equal footing, one drawing at a time.',
          'Recommended: 3 to 6 players, beyond that the waiting gets long.',
        ],
      },
      {
        heading: 'Battle royale',
        blocks: [
          'The shared hunt, with one player leaving every round.',
          'Same shared painting, same simultaneous hunt. But no points are counted here: the only question is who stays. The player whose patch is uncovered first leaves the game. The others keep hunting to the end of the round, which no longer eliminates anyone but gives each of them a rank, shown on their square. If nobody is found, nobody leaves.',
          'Best for closing an evening: the tension rises as the table empties, and the last round is a duel.',
          'Recommended: 4 to 10 players.',
        ],
      },
      {
        heading: 'Contest',
        blocks: [
          'The only mode where nobody searches.',
          'Everyone repaints the same square of the same painting. Then each player browses the others’ patches at their own pace and votes for the one they find best. A vote received earns its author one point. During the vote the authors are anonymous, and you cannot vote for yourself: names and votes only come out at the reveal.',
          'Best when the group is mixed, or when some players would rather draw without the pressure of the hunt clock.',
          'Recommended: 3 to 10 players.',
        ],
      },
    ],
  },
  modeTable: [
    { name: 'Shared hunt', players: '3 to 10', duration: '15 to 20 min' },
    { name: 'Stream', players: '4 to 10', duration: '15 to 20 min' },
    { name: 'One painting each', players: '2 to 10', duration: '15 to 20 min' },
    { name: 'One draws', players: '3 to 6', duration: '20 to 35 min' },
    { name: 'Battle royale', players: '4 to 10', duration: '20 to 30 min' },
    { name: 'Contest', players: '3 to 10', duration: '10 to 15 min' },
  ],
  modeTableHeadings: ['Mode', 'Players', 'Length'],
  premium: {
    heading: 'Free modes and paid modes',
    blocks: [
      'The shared hunt and stream mode are free, permanently.',
      'The other four will become paid when you CREATE a game, and only then. Joining one will always stay free: it takes a single person at the table to have unlocked the game for everyone to play every mode, with no account and no payment.',
      'Unlocking will be a one-off purchase, with no subscription, and it will also remove advertising for the account that made it. In the meantime, every mode is open to everyone.',
    ],
  },

  blog: [
    {
      slug: 'quest-ce-que-retusch',
      title: 'What is Retusch?',
      date: '2026-08-21',
      summary:
        'An online multiplayer drawing game, close to Gartic Phone in shape, but whose mechanic owes more to Meccha Chameleon: instead of being recognised, you try to disappear.',
      sections: [
        {
          heading: 'A drawing game where being recognised loses',
          blocks: [
            'Retusch is an online drawing game for 2 to 10 players. Each round hands you an illustration with one square erased, and you have to repaint it so it blends into the artwork. Then everyone looks at the retouched paintings and hunts for the repainted squares.',
            'What sets it apart fits in one sentence: winning means not being seen.',
          ],
        },
        {
          heading: 'What it shares with Gartic Phone',
          blocks: [
            'The shape is the same: you open a link, type a nickname, and play. No account, no install, no client to download. The game runs in the browser and a session starts in seconds. You invite friends by pasting a link into a conversation, and the table fills up.',
            'The rhythm is familiar too: short rounds, a clock, a drawing phase and then a phase where you discover what the others produced. And above all, that collective reveal at the end of a round where everyone comments on what they see. It is the same group pleasure.',
            'As in Gartic Phone, being good at drawing is not required. A well placed square of sky beats a masterpiece that spills over.',
          ],
        },
        {
          heading: 'What it borrows from Meccha Chameleon',
          blocks: [
            'Underneath, though, the mechanic sits elsewhere. In Meccha Chameleon, a three dimensional game on Steam, you paint your own character and then place them in a scene hoping nobody can tell them apart from their surroundings. Victory comes from resemblance and good placement, not from action.',
            'Retusch takes that idea and brings it back to the image. You do not choose where to hide, the game hands you the square; on the other hand you build everything that goes into it. The hiding place works if the eye slides over it without stopping.',
            'And as in any camouflage game, the tension comes from the double role. You are hidden and hunter at the same time: while you scan the painting for a suspicious patch, somebody is scanning yours.',
          ],
        },
        {
          heading: 'What exists nowhere else',
          blocks: [
            'The scoring rewards both camps in opposite ways. The hunter scores more the faster they find; the artist scores more the longer their square resists. A patch nobody sees earns its author the maximum.',
            'That produces a very particular way of playing: you are not trying to make something beautiful, you are trying to make something right. Picking the correct colour, continuing a line in the right place, leaving no corner empty. These are acts of observation before they are acts of drawing.',
            'The easiest way to understand it in practice is to read the full rules, or to start a quick game: it launches with no settings and no waiting.',
          ],
        },
      ],
    },
    {
      slug: 'comment-etre-imbattable',
      title: 'How to become unbeatable at Retusch',
      date: '2026-08-21',
      summary:
        'Keyboard shortcuts, working from broad to fine, and the empty corners mistake: three things that separate a beginner from a player nobody can find any more.',
      sections: [
        {
          heading: 'Learn the shortcuts and you double your useful time',
          blocks: [
            'Two minutes of drawing goes very fast when you fetch every tool with the mouse. Shortcuts easily win back twenty or thirty seconds a round, and those are the seconds you will spend on detail.',
            {
              list: [
                'B for the brush, P for the paint bucket, I for the eyedropper, H for the hand.',
                '1, 2 and 3 for the three brush sizes, without going through the toolbar.',
                'Ctrl+Z, or Cmd+Z on a Mac, to undo one stroke at a time.',
                'Holding Alt switches to the eyedropper and hands back to the previous tool as soon as you let go. It is the most useful of them all: you pick a colour mid action without ever changing tools.',
              ],
            },
            'A word on AZERTY keyboards: the 1, 2 and 3 keys work without Shift, even though your keyboard prints &, é and " there. The game reads the position of the key, not the character it produces.',
          ],
        },
        {
          heading: 'Paint from broad to fine',
          blocks: [
            'The classic beginner mistake is to start with one corner and polish it. The clock runs out, three quarters of the square is still background, and the patch is visible from across the room.',
            'The right method is the reverse. Start with a big brush, or the paint bucket, and lay down the large masses of colour: the sky, the wall, the shadow on the ground. In twenty seconds your square already has the right overall look and no longer reads as a foreign block.',
            'Only then reduce the size and handle the transitions: the horizon line crossing through, the edge of an object, the shade that changes halfway up. If you run out of time, you will have lost detail, not the round.',
            'Always pick your colours with the eyedropper rather than choosing them from the palette. An approximate shade is enough to give away an entire square, whereas the exact shade makes it invisible.',
          ],
        },
        {
          heading: 'Never leave the corners empty',
          blocks: [
            'This is clue number one, and the first thing experienced players look for. A square with a well treated middle but four corners left at the background colour draws a perfect rectangle on screen. You do not even need to look at the drawing: the geometry leaps out.',
            'Get into the habit of treating the outline first, then the middle. A well blended frame forgives a rough interior; the reverse is never true.',
            'Deliberately paint past the edges. The game clips your stroke at the boundary of the square, so you risk nothing by going well beyond, and it is the only way to be certain no pixel of background survives in a corner.',
          ],
        },
        {
          heading: 'And when it is your turn to hunt',
          blocks: [
            'Sweep the whole painting first without zooming: a badly blended square looks like a stain, and zoom only serves to confirm. Then follow the long lines, the ones that cross the image: a border shifted by a hair is almost always a patch.',
            'Finally, remember you only get one mistake per round. Three seconds of checking cost less than the whole round.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-entre-amis',
      title: 'A drawing game to play with friends',
      date: '2026-08-21',
      summary:
        'Why Retusch works well in a small group, how to invite by pasting a link, and why one player is enough to unlock the paid modes for the whole table.',
      sections: [
        {
          heading: 'A game that asks nothing of anyone',
          blocks: [
            'The biggest obstacle to a remote game night is setup. An account to create, a client to download, a version that does not match: half the group drops out before playing.',
            'Retusch runs in the browser. Your friends click a link, type a nickname and find themselves at the table. There is nothing to install, nothing to create, and it works as well on a computer as on a phone.',
            'Something else that matters in a group: nobody is left out for not being able to draw. The game does not ask you to depict anything, only to make a square match what surrounds it. The person who draws badly but observes well often wins.',
          ],
        },
        {
          heading: 'Inviting, in practice',
          blocks: [
            'Create a room from the home screen. Uncheck “public room” if you want to stay among yourselves: the invite link then becomes the only way in.',
            'Copy the link with the button provided, then paste it into your group chat, your voice channel or your messaging app. It contains the room code: your friends land straight at your table.',
            'You can set the game up while people arrive: the mode, the number of rounds, the drawing time, the size of the square and even the image categories. Nothing is locked until you start.',
          ],
        },
        {
          heading: 'One person pays, everyone plays',
          blocks: [
            'Two modes are free forever: the shared hunt, which is the default, and stream mode.',
            'The others will become paid, but only when CREATING a game. Joining will stay free, with no conditions. In practice, it takes a single person in the group to have unlocked the game for the whole table to enjoy every mode, with no account and nothing to pay.',
            'Unlocking is a one-off purchase, with no subscription.',
          ],
        },
        {
          heading: 'Which mode for which group',
          blocks: [
            'With three or four players, the shared hunt is enough and is understood within one round. From six players up it becomes genuinely good: discoveries come one after another and the screen never stops moving.',
            'To close an evening, try battle royale: the table empties round after round and the last one is a duel. For a mixed group, where some would rather draw quietly, contest removes all the pressure of the hunt clock.',
          ],
        },
      ],
    },
    {
      slug: 'jeu-de-dessin-en-stream',
      title: 'A drawing game to play on stream',
      date: '2026-08-21',
      summary:
        'Retusch has a mode built for live shows, where the streamer hunts alone while their audience hides. How to use it, and which other modes work in front of an audience.',
      sections: [
        {
          heading: 'A mode built for it',
          blocks: [
            'Stream mode flips the roles. Every player draws on the same painting except the host, who does not draw at all. Then the host is the only one hunting, while their audience watches.',
            'This is exactly the situation a live show needs, and for one specific reason: because the host does not draw, their own hiding place does not exist, so the chat has seen nothing it should not have. The audience does know where the other players hid, though, and can watch the host walk past three times in a row.',
            'Two rules were written for this case. The streamer scores no points and does not appear in the standings: the game is played between those who hide, the streamer runs the show. And they can be wrong as often as they like, whereas every other mode stops at the first mistake. A live show cannot end on one unlucky click.',
            'Stream mode is free, and will stay that way.',
          ],
        },
        {
          heading: 'Contest, the second good idea',
          blocks: [
            'In contest mode, everyone repaints the same square of the same painting, then each player votes for the patch they prefer. There is nothing left to find, only to judge.',
            'In front of an audience it works very well, and for the same reason as stream mode: there is nothing to find, so nothing the chat can give away. The entries compare side by side, the vote provides a moment of suspense, and revealing the names gives a clean ending. It is also the shortest mode.',
          ],
        },
        {
          heading: 'And the other modes',
          blocks: [
            'Nothing forces you into a dedicated mode, but then you are better off playing with friends than with the chat. In every other mode the streamer paints their own square on screen: the audience watches it being built and knows the answer before the hunt even begins.',
            'A game among friends, on the other hand, is very watchable from the outside: the drawing phase shows each player building their hiding place, and the reveal uncovers every square at once, with the name of its author.',
          ],
        },
        {
          heading: 'Bringing the chat into the game',
          blocks: [
            'The invite link is the only thing to share. Paste it into the chat and the first to arrive fill the table: no account to create, no download, no queue.',
            'The player cap is set in the room, between 2 and 10. With a large audience, open several short games rather than one long one: a round lasts under three minutes.',
          ],
        },
      ],
    },
    {
      slug: 'nouveautes',
      title: 'What is new in Retusch',
      date: '2026-08-21',
      summary:
        'The gameplay changes made to Retusch, newest first: new modes, new rules, improvements to drawing and hunting.',
      sections: [
        {
          heading: 'August 2026',
          blocks: [
            {
              list: [
                'The square to repaint no longer starts as a white block, but as the dominant colour of whatever it covers. You no longer lose half your time repainting a background that does not exist in the painting.',
                'The paint bucket no longer leaves a pale outline along strokes: it now slides under their softened edge.',
                'Battle royale no longer counts points. The first player uncovered leaves the game, and each player gets a round rank shown on their square, from red to green.',
                'Stream mode gives the streamer as many attempts as they want: their first miss no longer ends the round.',
                'In spectator mode, you can now pan and zoom into the painting you are watching being built.',
                'Emoji reactions now fly out from their author’s nickname in the player list, instead of rising from the bottom of the screen.',
                'Outside the square to repaint, the brush and the bucket move the view: no more switching to the hand tool to get around.',
                'The game name, top left during a game, takes you back home after confirmation.',
                'Japanese and Korean join the fifteen languages already available.',
              ],
            },
          ],
        },
        {
          heading: 'July 2026',
          blocks: [
            {
              list: [
                'New contest mode: everyone repaints the same square, then each player votes for the patch they prefer.',
                'New stream mode, built for live shows: the host hunts alone while their audience hides.',
                'Image categories replace difficulty levels. You tick the ones you want to see, and each category shows an example painting.',
                'Two rounds in a row no longer land on the same painting, and the ticked categories alternate from one round to the next.',
                'The end of game gallery lets you replay a patch stroke by stroke, and export it as an image or an animated GIF.',
              ],
            },
          ],
        },
      ],
    },
  ],

  ui: {
    atAGlance: 'At a glance',
    allArticles: 'All articles',
    theRules: 'The rules',
    theModes: 'The game modes',
  },
};
